import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Upload, Link, MoveUp, MoveDown } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const AdminHeroSlides = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [useUrlInput, setUseUrlInput] = useState(true);
  
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    button_text: '',
    button_link: '',
    image_url: '',
    display_order: 0,
    active: true
  });

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/hero-slides`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlides(response.data);
    } catch (error) {
      console.error('Error fetching slides:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API_BASE_URL}/api/admin/upload`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setFormData(prev => ({ ...prev, image_url: response.data.imageUrl }));
        setImagePreview(response.data.imageUrl);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      if (editingSlide) {
        await axios.put(`${API_BASE_URL}/api/admin/hero-slides/${editingSlide.id}`, formData, config);
      } else {
        await axios.post(`${API_BASE_URL}/api/admin/hero-slides`, formData, config);
      }
      fetchSlides();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving slide:', error);
      alert('Error saving slide. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this slide?')) {
      const token = localStorage.getItem('adminToken');
      try {
        await axios.delete(`${API_BASE_URL}/api/admin/hero-slides/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchSlides();
      } catch (error) {
        console.error('Error deleting slide:', error);
        alert('Error deleting slide. Please try again.');
      }
    }
  };

  const handleEdit = (slide) => {
    setEditingSlide(slide);
    setFormData({
      title: slide.title || '',
      subtitle: slide.subtitle || '',
      button_text: slide.button_text || '',
      button_link: slide.button_link || '',
      image_url: slide.image_url || '',
      display_order: slide.display_order || 0,
      active: slide.active !== false
    });
    setImagePreview(slide.image_url || '');
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingSlide(null);
    setFormData({
      title: '',
      subtitle: '',
      button_text: '',
      button_link: '',
      image_url: '',
      display_order: 0,
      active: true
    });
    setImagePreview('');
    setUseUrlInput(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Hero Slides</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add New Slide</span>
        </button>
      </div>

      {/* Slides List */}
      <div className="space-y-4">
        {slides.map((slide, index) => (
          <div key={slide.id} className="bg-dark-900 rounded-lg overflow-hidden border border-dark-700">
            <div className="flex flex-col md:flex-row">
              <div className="md:w-48 h-32">
                <img src={slide.image_url} alt={slide.title} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-bold text-white">{slide.title}</h3>
                    <p className="text-gray-400 text-sm">{slide.subtitle}</p>
                    <div className="flex items-center space-x-4 mt-2">
                      <span className="text-xs text-gray-500">Order: {slide.display_order}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${slide.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {slide.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => handleEdit(slide)} className="text-blue-400 hover:text-blue-300">
                      <Edit2 size={18} />
                    </button>
                    <button onClick={() => handleDelete(slide.id)} className="text-red-400 hover:text-red-300">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Form */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-dark-700">
            <div className="p-4 border-b border-dark-700 flex justify-between items-center sticky top-0 bg-dark-800">
              <h2 className="text-xl font-bold text-white">
                {editingSlide ? 'Edit Slide' : 'Add New Slide'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">Title *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">Subtitle</label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Button Text</label>
                  <input
                    type="text"
                    value={formData.button_text}
                    onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Button Link</label>
                  <input
                    type="text"
                    value={formData.button_link}
                    onChange={(e) => setFormData({ ...formData, button_link: e.target.value })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                  />
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Slide Image</label>
                <div className="flex space-x-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setUseUrlInput(true)}
                    className={`flex-1 py-2 rounded-lg text-sm ${useUrlInput ? 'bg-orange-500 text-white' : 'bg-dark-700 text-gray-400'}`}
                  >
                    Use URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseUrlInput(false)}
                    className={`flex-1 py-2 rounded-lg text-sm ${!useUrlInput ? 'bg-orange-500 text-white' : 'bg-dark-700 text-gray-400'}`}
                  >
                    Upload File
                  </button>
                </div>
                {useUrlInput ? (
                  <input
                    type="text"
                    value={formData.image_url}
                    onChange={(e) => {
                      setFormData({ ...formData, image_url: e.target.value });
                      setImagePreview(e.target.value);
                    }}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white"
                    placeholder="https://example.com/image.jpg"
                  />
                ) : (
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white file:mr-2 file:py-1 file:px-3 file:rounded file:bg-orange-500 file:text-white"
                    disabled={uploading}
                  />
                )}
                {uploading && <p className="text-gray-400 text-sm mt-1">Uploading...</p>}
                {imagePreview && (
                  <img src={imagePreview} alt="Preview" className="mt-3 w-32 h-24 object-cover rounded" />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Display Order</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white"
                  />
                </div>
                <div className="flex items-center space-x-2 mt-6">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-orange-500"
                  />
                  <label htmlFor="active" className="text-sm text-gray-300">Active</label>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button type="submit" className="flex-1 bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition">
                  {editingSlide ? 'Update' : 'Add'} Slide
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-dark-600 py-2 rounded-lg hover:bg-dark-700 transition text-gray-300">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHeroSlides;